import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common';
import axios from 'axios';

interface BrokerProfileData {
  id: string;
  name: string;
  logo_url: string;
  description: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  website: string;
  license_number: string;
  public_profile_slug: string;
}

export function BrokerProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [broker, setBroker] = useState<BrokerProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    const loadProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/policy/api/public/brokers/${slug}/`);
        setBroker(response.data);
      } catch {
        setError('Broker not found');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !broker) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Broker Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The broker profile you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/brokers-directory" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
          Browse all brokers
        </Link>
      </div>
    );
  }

  const address = [broker.address_line1, broker.address_line2, broker.city, broker.state, broker.pincode]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-6">
            {broker.logo_url && (
              <img
                src={broker.logo_url}
                alt={broker.name}
                className="h-20 w-20 rounded-lg object-contain bg-gray-100 dark:bg-gray-700 p-2"
              />
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{broker.name}</h1>
              {broker.license_number && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  License: {broker.license_number}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* About */}
        {broker.description && (
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">About</h2>
            <p className="text-gray-700 dark:text-gray-300">{broker.description}</p>
          </section>
        )}

        {/* Contact Info */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {broker.website && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Website</span>
                <a href={broker.website} target="_blank" rel="noopener noreferrer" className="block text-blue-600 dark:text-blue-400 hover:underline">
                  {broker.website}
                </a>
              </div>
            )}
            {broker.phone && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</span>
                <p className="text-gray-900 dark:text-white">{broker.phone}</p>
              </div>
            )}
            {broker.email && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</span>
                <a href={`mailto:${broker.email}`} className="block text-blue-600 dark:text-blue-400 hover:underline">
                  {broker.email}
                </a>
              </div>
            )}
            {address && (
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Address</span>
                <p className="text-gray-900 dark:text-white">{address}</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA.

            BOARD #266b, review follow-up. The origination page is registered at
            `/public/quote/:brokerSlug` in App.tsx and works, but NOTHING in the
            SPA linked to it -- `grep -rn "/public/quote/" src/` returned only
            the route line and the page's own docstring. A page a visitor can
            only reach by being handed the URL is one step away from the dead
            code this repo keeps shipping, and this section -- already titled
            "Get a Quote" and offering only a mailto -- is where a visitor
            standing on the broker's profile looks for it.

            The slug is the broker's OWN `public_profile_slug` from the API
            response, not the URL param, so the link carries the value
            `get_public_broker_or_none` resolves on the other side; the param is
            only the fallback for an older payload that omits it. */}
        <section className="bg-blue-600 dark:bg-blue-700 rounded-lg shadow p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Get a Quote</h2>
          <p className="text-blue-100 mb-6">
            Get a Motor, Health or Travel quote online, or contact us for personalised insurance
            solutions
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {(broker.public_profile_slug || slug) && (
              <Link
                to={`/public/quote/${broker.public_profile_slug || slug}`}
                data-testid="broker-profile-quote-cta"
                className="inline-flex items-center rounded-md bg-white px-6 py-3 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50 transition-colors"
              >
                Get a quote
              </Link>
            )}
            <a
              href={`mailto:${broker.email}`}
              className="inline-flex items-center rounded-md border border-white/70 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Powered by base.Policy.online
          </p>
        </div>
      </footer>
    </div>
  );
}
